import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Gem, Play, Square, RefreshCw, DollarSign, Target, Shield, Loader2,
  ArrowUpRight, ArrowDownRight, Zap, HeartPulse, AlertTriangle,
  Power, Skull, RotateCcw, Activity, Clock, TrendingUp, Lock, Unlock,
  Flame, Gauge, Layers, Brain, CheckCircle,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API } from '../config';

const PnlText = ({ value, className = "" }) => {
  if (value === null || value === undefined) return <span className="text-zinc-500">---</span>;
  const p = value >= 0;
  return <span className={`font-mono ${p ? "text-emerald-400" : "text-red-400"} ${className}`}>{p ? "+" : ""}${Number(value).toFixed(2)}</span>;
};

/* ============ TIER DEFINITIONS (mirror backend) ============ */
const TIER_UI = {
  SAFE: {
    color: "emerald",
    bg: "bg-emerald-950/30",
    border: "border-emerald-700/40",
    badgeCls: "bg-emerald-500/20 text-emerald-300",
    icon: Shield,
    glow: "",
  },
  BOOST: {
    color: "amber",
    bg: "bg-amber-950/20",
    border: "border-amber-600/40",
    badgeCls: "bg-amber-500/20 text-amber-300",
    icon: Gauge,
    glow: "shadow-[0_0_15px_rgba(245,158,11,0.15)]",
  },
  BEAST: {
    color: "red",
    bg: "bg-red-950/30",
    border: "border-red-600/50",
    badgeCls: "bg-red-500/20 text-red-300",
    icon: Flame,
    glow: "shadow-[0_0_20px_rgba(239,68,68,0.2)]",
  },
};

/* ============ CONFIRMATION MODAL ============ */
const DangerModal = ({ tier, tierConfig, balance, onConfirm, onCancel }) => {
  const [typed, setTyped] = useState("");
  const confirmWord = tier === "BEAST" ? "I ACCEPT THE RISK" : "CONFIRM";
  const worstDD = ((tierConfig?.projected_dd_pct || 0) / 100) * (balance || 1000);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="danger-modal">
      <div className="bg-zinc-900 border border-red-700/60 rounded-xl max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-red-950/60 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-red-300">DANGER ZONE</h3>
            <p className="text-xs text-zinc-500">Leverage tier change</p>
          </div>
        </div>

        <div className="bg-red-950/40 border border-red-800/40 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-3 gap-3 text-center text-xs">
            <div>
              <p className="text-zinc-500">Leverage</p>
              <p className="text-2xl font-bold text-red-300 font-mono">{tierConfig?.effective_lev || tierConfig?.leverage}x</p>
            </div>
            <div>
              <p className="text-zinc-500">Projected DD</p>
              <p className="text-2xl font-bold text-red-400 font-mono">~{tierConfig?.projected_dd_pct}%</p>
            </div>
            <div>
              <p className="text-zinc-500">Hard Stop</p>
              <p className="text-2xl font-bold text-red-400 font-mono">-{tierConfig?.max_dd_pct}%</p>
            </div>
          </div>

          <div className="bg-black/40 rounded-lg p-3 text-center">
            <p className="text-sm text-red-300 font-medium">
              At {tierConfig?.effective_lev || tierConfig?.leverage}x leverage your worst historical drawdown becomes
            </p>
            <p className="text-3xl font-bold text-red-400 font-mono mt-1">
              ${worstDD.toFixed(0)}
            </p>
            <p className="text-xs text-red-400/70 mt-1">
              on a ${(balance || 1000).toLocaleString()} account.{" "}
              {tier === "BEAST" ? "You will feel this." : "Proceed with caution."}
            </p>
          </div>

          <div className="text-[11px] text-zinc-500 space-y-1">
            <p>DD hard stop auto-tightens: 1x=-13% | 1.5x=-10% | 2x=-8%</p>
            <p>Resets to SAFE on every session restart.</p>
            <p>Sharpe drops from 2.93 to ~{tierConfig?.sharpe} at this leverage.</p>
          </div>
        </div>

        <div>
          <p className="text-xs text-zinc-400 mb-2">
            Type <span className="text-red-400 font-mono font-bold">"{confirmWord}"</span> to proceed:
          </p>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="w-full bg-black/60 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200 font-mono focus:border-red-600 focus:outline-none"
            placeholder={confirmWord}
            data-testid="danger-confirm-input"
          />
        </div>

        <div className="flex gap-3">
          <Button onClick={onCancel} variant="outline" className="flex-1 border-zinc-700 text-zinc-400" data-testid="danger-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={typed !== confirmWord}
            className="flex-1 bg-red-700 hover:bg-red-600 text-white disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="danger-confirm-btn"
          >
            <Skull className="w-4 h-4 mr-2" /> Activate {tier}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ============ TIER CARD ============ */
const TierCard = ({ tierKey, tierConfig, isActive, isLocked, paperDays, balance, onSelect }) => {
  const ui = TIER_UI[tierKey];
  const Icon = ui.icon;
  const locked = isLocked && tierKey !== "SAFE";

  return (
    <button
      onClick={() => !locked && !isActive && onSelect(tierKey)}
      disabled={locked || isActive}
      className={`relative text-left rounded-xl border p-4 transition-all duration-300 w-full
        ${isActive ? `${ui.bg} ${ui.border} ${ui.glow} ring-1 ring-${ui.color}-500/30` : "bg-zinc-900/40 border-zinc-800 hover:border-zinc-600"}
        ${locked ? "opacity-40 cursor-not-allowed" : isActive ? "cursor-default" : "cursor-pointer"}
      `}
      data-testid={`tier-card-${tierKey.toLowerCase()}`}
    >
      {locked && (
        <div className="absolute top-3 right-3">
          <Lock className="w-4 h-4 text-zinc-600" />
        </div>
      )}
      {isActive && (
        <div className="absolute top-3 right-3">
          <Badge className={`text-[10px] ${ui.badgeCls}`}>ACTIVE</Badge>
        </div>
      )}
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 text-${ui.color}-400`} />
        <span className="font-bold text-zinc-100">{tierConfig.label}</span>
        <span className="text-xs text-zinc-500 font-mono">{tierConfig.effective_lev || tierConfig.leverage}x</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs mb-2">
        <div>
          <p className="text-zinc-500 text-[10px]">Proj. DD</p>
          <p className={`font-mono font-bold ${tierKey === "SAFE" ? "text-emerald-400" : tierKey === "BOOST" ? "text-amber-400" : "text-red-400"}`}>
            ~{tierConfig.projected_dd_pct}%
          </p>
        </div>
        <div>
          <p className="text-zinc-500 text-[10px]">Sharpe</p>
          <p className="font-mono font-bold text-zinc-200">{tierConfig.sharpe}</p>
        </div>
        <div>
          <p className="text-zinc-500 text-[10px]">DD Stop</p>
          <p className="font-mono font-bold text-zinc-300">-{tierConfig.max_dd_pct}%</p>
        </div>
      </div>
      <p className="text-[10px] text-zinc-500">{tierConfig.description}</p>
      {locked && (
        <p className="text-[10px] text-amber-500/70 mt-1 flex items-center gap-1">
          <Lock className="w-3 h-3" /> {30 - paperDays} days of paper trading remaining
        </p>
      )}
    </button>
  );
};

/* ============ REGIME ADVISOR PANEL ============ */
const REGIME_COLORS = {
  TREND_UP: "text-emerald-400",
  TREND_DOWN: "text-red-400",
  RANGE_LOW_VOL: "text-blue-400",
  RANGE_HIGH_VOL: "text-orange-400",
  BREAKOUT_EXPANSION: "text-yellow-400",
  PANIC_LIQUIDATION: "text-rose-400",
  MEAN_REVERTIVE_CHOP: "text-violet-400",
};

const RegimeAdvisorPanel = ({ advisor, loading, onRefresh }) => {
  if (loading) return <div className="text-zinc-500 text-xs py-2 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading regime advisor...</div>;
  if (!advisor) return null;

  const recs = advisor?.recommendations || [];
  const active = recs.filter(r => r.action === "DEPLOY");
  const idle = recs.filter(r => r.action === "CASH");

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="regime-advisor">
      <CardHeader className="py-3 px-5">
        <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
          <Brain className="w-4 h-4 text-cyan-400" /> Regime Portfolio Advisor
          <Badge className="text-[10px] bg-cyan-500/10 text-cyan-300 ml-2">
            {active.length} active / {idle.length} idle
          </Badge>
          <span className="text-[10px] text-zinc-500 ml-auto">Capital: ${(advisor.total_capital || 0).toLocaleString()}</span>
          <Button size="sm" variant="outline" onClick={onRefresh} className="ml-2 border-zinc-700 text-zinc-400 h-6 px-2">
            <RefreshCw className="w-3 h-3" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-4">
        <div className="grid grid-cols-4 gap-3">
          {recs.map(r => {
            const rc = REGIME_COLORS[r.regime] || "text-zinc-500";
            const isActive = r.action === "DEPLOY";
            return (
              <div key={r.asset} className={`rounded-lg border p-3 ${isActive ? "bg-emerald-950/20 border-emerald-700/30" : "bg-zinc-800/30 border-zinc-700/30"}`}
                   data-testid={`advisor-${r.asset}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-mono font-bold text-zinc-100 text-sm">{r.asset.replace("USDT","")}</span>
                  <Badge className={`text-[9px] ${isActive ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-600/20 text-zinc-500"}`}>
                    {r.action}
                  </Badge>
                </div>
                <p className={`text-[11px] font-mono mb-1 ${rc}`}>{r.regime?.replace(/_/g, " ")}</p>
                {isActive ? (
                  <div className="space-y-1">
                    <p className="text-[10px] text-emerald-300 font-mono truncate">{r.emerald_strategy}</p>
                    <div className="flex justify-between text-[9px]">
                      <span className="text-zinc-500">R.Sharpe: <span className="text-emerald-400">{r.regime_sharpe?.toFixed(1)}</span></span>
                      <span className="text-zinc-500">Alloc: <span className="text-emerald-300">{r.allocation_pct}%</span></span>
                    </div>
                    <p className="text-[9px] text-zinc-600">${(r.allocation_usd || 0).toLocaleString()}</p>
                  </div>
                ) : (
                  <p className="text-[9px] text-zinc-600 mt-1">No EMERALD for this regime</p>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

/* ============ MAIN PAGE ============ */
export default function MissionControlPage() {
  const { token } = useAuth();
  const [liveStatus, setLiveStatus] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [killing, setKilling] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [tierChanging, setTierChanging] = useState(false);
  const [pendingTier, setPendingTier] = useState(null);
  const [useTestnet, setUseTestnet] = useState(false);
  const [advisor, setAdvisor] = useState(null);
  const [advisorLoading, setAdvisorLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/live/status`, { headers });
      setLiveStatus(res.data);
    } catch { /* */ }
    setLoading(false);
  };

  const fetchTrades = async () => {
    try {
      const res = await axios.get(`${API}/live/trades?limit=20`, { headers });
      setTrades(res.data.trades || []);
    } catch { /* */ }
  };

  const fetchAdvisor = async () => {
    setAdvisorLoading(true);
    try {
      const res = await axios.get(`${API}/regime/advisor`, { headers });
      setAdvisor(res.data);
    } catch { /* */ }
    setAdvisorLoading(false);
  };

  const initEngine = async () => {
    try {
      setInitializing(true);
      await axios.post(`${API}/live/initialize?testnet=${useTestnet}`, {}, { headers });
      toast.success(`Engine connected ${useTestnet ? "(TESTNET)" : "(LIVE)"}!`);
      fetchStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to connect";
      if (msg.includes("geo-restricted")) {
        toast.error("Binance is geo-restricted from this server. Try enabling Testnet mode.");
        setUseTestnet(true);
      } else {
        toast.error(msg);
      }
    } finally {
      setInitializing(false);
    }
  };

  const startLive = async () => {
    try {
      setStarting(true);
      const res = await axios.post(`${API}/live/start?risk_pct=1.0`, {}, { headers });
      toast.success(`LIVE trading started! Mode: ${res.data.mode}`);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start");
    } finally {
      setStarting(false);
    }
  };

  const stopLive = async () => {
    try {
      setStopping(true);
      await axios.post(`${API}/live/stop`, {}, { headers });
      toast.success("Live trading stopped.");
      fetchStatus();
      fetchTrades();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to stop");
    } finally {
      setStopping(false);
    }
  };

  const killSwitch = async () => {
    if (!window.confirm("KILL SWITCH: This will close ALL positions and cancel ALL orders immediately. Continue?")) return;
    try {
      setKilling(true);
      await axios.post(`${API}/live/kill-switch`, {}, { headers });
      toast.error("KILL SWITCH ACTIVATED. All trading halted.");
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kill switch failed");
    } finally {
      setKilling(false);
    }
  };

  const resetKill = async () => {
    try {
      await axios.post(`${API}/live/reset-kill-switch`, {}, { headers });
      toast.success("Kill switch reset. Tier reset to SAFE.");
      fetchStatus();
    } catch { /* */ }
  };

  const handleTierSelect = (tier) => {
    if (tier === "SAFE") {
      confirmTierChange("SAFE");
    } else {
      setPendingTier(tier);
    }
  };

  const confirmTierChange = async (tier) => {
    try {
      setTierChanging(true);
      await axios.post(`${API}/live/set-tier`, { tier, confirmed: true }, { headers });
      toast.success(`Tier changed to ${tier}`);
      setPendingTier(null);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Tier change failed");
    } finally {
      setTierChanging(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchTrades();
    fetchAdvisor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (liveStatus?.status !== "running") return;
    const interval = setInterval(() => { fetchStatus(); fetchTrades(); }, 8000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveStatus?.status]);

  const isRunning = liveStatus?.status === "running";
  const engine = liveStatus?.engine || {};
  const account = liveStatus?.account || {};
  const agents = liveStatus?.agents || {};
  const summary = liveStatus?.summary || {};
  const mode = liveStatus?.mode || "---";
  const isKilled = engine.kill_switch_active;
  const currentTier = engine.current_tier || "SAFE";
  const allTiers = engine.all_tiers || {};
  const paperDays = liveStatus?.paper_days || 0;
  const boostLocked = paperDays < 30;
  const tierUI = TIER_UI[currentTier] || TIER_UI.SAFE;
  const accountBalance = account.balance || engine.starting_balance || 1000;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="mc-loading">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
        <span className="ml-3 text-zinc-400 font-mono">Loading Mission Control...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mission-control-page">
      {/* Danger Modal */}
      {pendingTier && allTiers[pendingTier] && (
        <DangerModal
          tier={pendingTier}
          tierConfig={allTiers[pendingTier]}
          balance={accountBalance}
          onConfirm={() => confirmTierChange(pendingTier)}
          onCancel={() => setPendingTier(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Power className="w-6 h-6 text-cyan-400" />
            Mission Control
            <Badge className={`text-xs ml-2 ${mode === "LIVE" ? "bg-red-500/20 text-red-300" : mode === "TESTNET" ? "bg-yellow-500/20 text-yellow-300" : "bg-zinc-700/40 text-zinc-400"}`} data-testid="mode-badge">
              {mode}
            </Badge>
            <Badge className={`text-xs ${tierUI.badgeCls}`} data-testid="tier-badge">
              {currentTier} {engine.leverage}x
            </Badge>
          </h1>
          <p className="text-xs text-zinc-500 mt-1">Regime-optimized autonomous trading on Binance Futures</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchStatus(); fetchTrades(); }} className="border-zinc-700 text-zinc-400" data-testid="mc-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>

          {!engine.initialized && (
            <Button size="sm" onClick={initEngine} disabled={initializing} className={`${useTestnet ? "bg-yellow-600 hover:bg-yellow-500" : "bg-cyan-600 hover:bg-cyan-500"} text-white`} data-testid="init-engine-btn">
              {initializing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
              {useTestnet ? "Connect Testnet" : "Connect Binance"}
            </Button>
          )}

          {engine.initialized && !isRunning && !isKilled && (
            <Button size="sm" onClick={startLive} disabled={starting} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="start-live-btn">
              {starting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}
              Start Live Trading
            </Button>
          )}

          {isRunning && (
            <Button size="sm" onClick={stopLive} disabled={stopping} className="bg-zinc-600 hover:bg-zinc-500 text-white" data-testid="stop-live-btn">
              {stopping ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Square className="w-3.5 h-3.5 mr-1" />}
              Stop Trading
            </Button>
          )}

          {isKilled ? (
            <Button size="sm" onClick={resetKill} className="bg-orange-600 hover:bg-orange-500 text-white" data-testid="reset-kill-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset Kill Switch
            </Button>
          ) : (
            <Button size="sm" onClick={killSwitch} disabled={killing} className="bg-red-700 hover:bg-red-600 text-white" data-testid="kill-switch-btn">
              {killing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Skull className="w-3.5 h-3.5 mr-1" />}
              KILL SWITCH
            </Button>
          )}
        </div>
      </div>

      {/* Kill Switch Alert */}
      {isKilled && (
        <div className="bg-red-950/30 border border-red-700/50 rounded-lg p-4 flex items-center gap-3" data-testid="kill-alert">
          <Skull className="w-6 h-6 text-red-400 shrink-0" />
          <div>
            <p className="text-red-300 font-bold text-sm">KILL SWITCH ACTIVE</p>
            <p className="text-red-400/70 text-xs">All trading halted. Reset kill switch to resume (resets tier to SAFE).</p>
          </div>
        </div>
      )}

      {/* Account & Engine Status */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-zinc-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Balance</p>
            <p className="font-mono text-lg font-bold text-zinc-100" data-testid="balance-value">${account.balance?.toLocaleString() || "---"}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-zinc-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Available</p>
            <p className="font-mono text-lg font-bold text-cyan-400">${account.available?.toLocaleString() || "---"}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-zinc-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Session P&L</p>
            <PnlText value={summary.total_pnl} className="text-lg font-bold" />
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-zinc-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Daily P&L</p>
            <PnlText value={engine.daily_pnl} className="text-lg font-bold" />
            <p className="text-[9px] text-zinc-600">DD: {engine.current_dd_pct || 0}% / {engine.max_portfolio_dd_pct || 13}%</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-zinc-500 flex items-center gap-1"><Target className="w-3 h-3" /> Trades</p>
            <p className="font-mono text-lg font-bold text-zinc-300">{summary.total_trades || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-zinc-500 flex items-center gap-1"><Shield className="w-3 h-3" /> Leverage</p>
            <p className="font-mono text-lg font-bold text-zinc-300" data-testid="leverage-value">{engine.leverage || 1}x</p>
          </CardContent>
        </Card>
      </div>

      {/* ============ REGIME PORTFOLIO ADVISOR ============ */}
      <RegimeAdvisorPanel advisor={advisor} loading={advisorLoading} onRefresh={fetchAdvisor} />

      {/* ============ BOOST MODE TIER SELECTOR ============ */}
      <Card className="bg-zinc-900/50 border-zinc-800" data-testid="boost-mode-section">
        <CardHeader className="py-3 px-5">
          <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
            <Gauge className="w-4 h-4 text-zinc-400" /> Leverage Tier
            <span className="text-[10px] text-zinc-500 font-normal ml-2">
              Paper trading: {paperDays}/30 days
              {boostLocked ? (
                <span className="text-amber-500 ml-1">(Boost/Beast locked)</span>
              ) : (
                <span className="text-emerald-400 ml-1">(All tiers unlocked)</span>
              )}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {Object.entries(allTiers).length > 0 ? (
              Object.entries(allTiers).map(([key, config]) => (
                <TierCard
                  key={key}
                  tierKey={key}
                  tierConfig={config}
                  isActive={currentTier === key}
                  isLocked={boostLocked}
                  paperDays={paperDays}
                  balance={accountBalance}
                  onSelect={handleTierSelect}
                />
              ))
            ) : (
              ["SAFE", "BOOST", "BEAST"].map((key) => {
                const fallback = {
                  SAFE: { effective_lev: 1, projected_dd_pct: 10, sharpe: 2.93, max_dd_pct: 13, label: "Safe", description: "Default" },
                  BOOST: { effective_lev: 1.5, projected_dd_pct: 15, sharpe: 2.40, max_dd_pct: 10, label: "Boost", description: "30 days required" },
                  BEAST: { effective_lev: 2, projected_dd_pct: 20, sharpe: 1.80, max_dd_pct: 8, label: "Beast", description: "Experienced only" },
                };
                return (
                  <TierCard
                    key={key}
                    tierKey={key}
                    tierConfig={fallback[key]}
                    isActive={currentTier === key}
                    isLocked={boostLocked}
                    paperDays={paperDays}
                    balance={accountBalance}
                    onSelect={handleTierSelect}
                  />
                );
              })
            )}
          </div>

          {/* DD Visualization Bar */}
          <div className="mt-4 space-y-1">
            <div className="flex justify-between text-[10px] text-zinc-500">
              <span>Drawdown Risk</span>
              <span>{engine.current_dd_pct || 0}% / {engine.max_portfolio_dd_pct || 13}% hard stop</span>
            </div>
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  (engine.current_dd_pct || 0) > (engine.max_portfolio_dd_pct || 13) * 0.7
                    ? "bg-red-500"
                    : (engine.current_dd_pct || 0) > (engine.max_portfolio_dd_pct || 13) * 0.4
                    ? "bg-amber-500"
                    : "bg-emerald-500"
                }`}
                style={{ width: `${Math.min(((engine.current_dd_pct || 0) / (engine.max_portfolio_dd_pct || 13)) * 100, 100)}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Binance Position */}
      {account.position && (
        <Card className={`border ${account.position.side === "LONG" ? "bg-emerald-950/10 border-emerald-800/30" : "bg-red-950/10 border-red-800/30"}`} data-testid="binance-position">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm flex items-center gap-2">
              {account.position.side === "LONG" ? <ArrowUpRight className="w-4 h-4 text-emerald-400" /> : <ArrowDownRight className="w-4 h-4 text-red-400" />}
              <span className={account.position.side === "LONG" ? "text-emerald-300" : "text-red-300"}>
                BINANCE {account.position.side} POSITION
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            <div className="grid grid-cols-6 gap-4 text-xs">
              <div><p className="text-zinc-500 text-[10px]">Size</p><p className="font-mono text-zinc-200">{account.position.quantity} ETH</p></div>
              <div><p className="text-zinc-500 text-[10px]">Entry</p><p className="font-mono text-zinc-200">${account.position.entry_price?.toFixed(2)}</p></div>
              <div><p className="text-zinc-500 text-[10px]">Mark</p><p className="font-mono text-zinc-200">${account.position.mark_price?.toFixed(2)}</p></div>
              <div><p className="text-zinc-500 text-[10px]">Unrealized P&L</p><PnlText value={account.position.unrealized_pnl} /></div>
              <div><p className="text-zinc-500 text-[10px]">Liquidation</p><p className="font-mono text-red-400">${account.position.liquidation_price?.toFixed(2) || "---"}</p></div>
              <div><p className="text-zinc-500 text-[10px]">Leverage</p><p className="font-mono text-zinc-300">{account.position.leverage}x</p></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agent Cards */}
      {Object.keys(agents).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {Object.values(agents).map((agent) => {
            const health = agent.health || {};
            const healthColor = {
              HEALTHY: "bg-emerald-500/20 text-emerald-300",
              WARNING: "bg-yellow-500/20 text-yellow-300",
              CRITICAL: "bg-red-500/20 text-red-300",
              PAUSED: "bg-orange-500/20 text-orange-300",
            }[health.status] || "bg-zinc-700/40 text-zinc-400";

            return (
              <Card key={agent.name} className={`border ${agent.is_running ? "bg-zinc-900/60 border-cyan-700/20" : "bg-zinc-900/40 border-zinc-800"}`} data-testid={`live-agent-${agent.name}`}>
                <CardContent className="py-4 px-5 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Gem className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm font-bold text-zinc-100">{agent.name}</span>
                      <Badge className={`text-[10px] ${agent.is_running ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-700/40 text-zinc-400"}`}>
                        {agent.health?.paused_by_health ? "PAUSED" : agent.is_running ? "LIVE" : "OFF"}
                      </Badge>
                    </div>
                    <Badge className={`text-[10px] ${healthColor}`}><HeartPulse className="w-3 h-3 mr-1 inline" />{health.status || "---"}</Badge>
                  </div>
                  <div className="grid grid-cols-5 gap-3 text-xs">
                    <div><p className="text-zinc-500 text-[10px]">P&L</p><PnlText value={agent.total_pnl} /></div>
                    <div><p className="text-zinc-500 text-[10px]">Win Rate</p><p className="font-mono text-zinc-300">{agent.win_rate?.toFixed(1)}%</p></div>
                    <div><p className="text-zinc-500 text-[10px]">Trades</p><p className="font-mono text-zinc-300">{agent.total_trades}</p></div>
                    <div><p className="text-zinc-500 text-[10px]">Sharpe R30</p><p className={`font-mono ${(health.rolling_sharpe || 0) >= 1.5 ? "text-emerald-400" : (health.rolling_sharpe || 0) >= 1.0 ? "text-yellow-400" : "text-zinc-400"}`}>{health.rolling_sharpe?.toFixed(2) || "---"}</p></div>
                    <div><p className="text-zinc-500 text-[10px]">DD</p><p className={`font-mono ${(health.drawdown_pct || 0) > 15 ? "text-red-400" : "text-zinc-300"}`}>{health.drawdown_pct?.toFixed(1) || 0}%</p></div>
                  </div>
                  {health.critical_reasons?.length > 0 && (
                    <div className="text-[10px] px-2 py-1 rounded bg-red-950/30 border border-red-800/30 text-red-400">
                      {health.critical_reasons.map((r, i) => <div key={i}><AlertTriangle className="w-3 h-3 inline mr-1" />{r}</div>)}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Not initialized */}
      {!engine.initialized && !isRunning && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12 text-center">
            <Power className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-zinc-300 mb-2">SKYNET Mission Control</h2>
            <p className="text-sm text-zinc-500 mb-4 max-w-md mx-auto">
              Connect to Binance Futures for regime-optimized autonomous trading.
              The Portfolio Engine deploys the best EMERALD strategy for each detected market regime.
            </p>
            <div className="flex items-center justify-center gap-3 mb-4">
              <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer" data-testid="testnet-toggle">
                <input
                  type="checkbox"
                  checked={useTestnet}
                  onChange={(e) => setUseTestnet(e.target.checked)}
                  className="w-4 h-4 rounded bg-zinc-800 border-zinc-600"
                />
                Testnet Mode {useTestnet && <Badge className="text-[10px] bg-yellow-500/20 text-yellow-300">TESTNET</Badge>}
              </label>
            </div>
            <Button onClick={initEngine} disabled={initializing} className={`${useTestnet ? "bg-yellow-600 hover:bg-yellow-500" : "bg-cyan-600 hover:bg-cyan-500"} text-white`} data-testid="init-engine-empty-btn">
              {initializing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Connect to Binance {useTestnet ? "(Testnet)" : ""}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Trade History */}
      {trades.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
              <Clock className="w-4 h-4 text-zinc-400" /> Live Trade History
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            <table className="w-full text-xs" data-testid="live-trades-table">
              <thead>
                <tr className="text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 text-left">Agent</th>
                  <th className="py-2 text-left">Side</th>
                  <th className="py-2 text-right">Entry</th>
                  <th className="py-2 text-right">Exit</th>
                  <th className="py-2 text-right">P&L</th>
                  <th className="py-2 text-left">Reason</th>
                  <th className="py-2 text-center">Health</th>
                  <th className="py-2 text-right">Time</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t, i) => (
                  <tr key={i} className="border-b border-zinc-800/50">
                    <td className="py-2 font-mono text-cyan-400">{t.strategy}</td>
                    <td className="py-2"><Badge className={`text-[10px] ${t.side === "LONG" ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>{t.side}</Badge></td>
                    <td className="py-2 text-right font-mono text-zinc-300">${t.entry_price?.toFixed(2)}</td>
                    <td className="py-2 text-right font-mono text-zinc-300">${t.exit_price?.toFixed(2)}</td>
                    <td className="py-2 text-right"><PnlText value={t.net_pnl} /></td>
                    <td className="py-2"><Badge className={`text-[10px] ${t.exit_reason === "TAKE_PROFIT" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>{t.exit_reason}</Badge></td>
                    <td className="py-2 text-center text-[10px] text-zinc-500">{t.health_status || ""}</td>
                    <td className="py-2 text-right text-zinc-500">{t.closed_at ? new Date(t.closed_at).toLocaleString() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Engine Info */}
      {engine.initialized && (
        <div className="text-[10px] text-zinc-600 flex items-center gap-4" data-testid="engine-info-bar">
          <span>Engine: Connected</span>
          <span>Symbol: {engine.symbol}</span>
          <span>Tier: {currentTier}</span>
          <span>DD Stop: -{engine.max_portfolio_dd_pct}%</span>
          <span>Position Cap: {((engine.max_position_pct || 0.01) * 100).toFixed(0)}%</span>
          <span>Mode: {mode}</span>
        </div>
      )}
    </div>
  );
}
